import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { ReviewTemplateTypeType, ReviewTemplateType, ReviewTemplateVersion } from 'Shared/types/ReviewTemplates.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { fetchReviewTemplates, createReviewTemplate } from 'Shared/actions/ReviewTemplates.actions';

// Selectors
import { getReviewTemplatesByTemplateType } from 'Shared/selectors/ReviewTemplates.selectors';

type Props = {
  templateType: ReviewTemplateTypeType;
  withFetch?: boolean;
};

export const useReviewTemplates = ({ templateType, withFetch = true }: Props) => {
  const dispatch = useDispatch();
  const { reviewTemplateBuilderV2 } = useFlags();

  const [isFetching, toggleIsFetching] = useToggle(false);
  const [isCreatingNew, toggleIsCreatingNew] = useToggle(false);

  const templates: ReviewTemplateType[] = useSelector(state =>
    getReviewTemplatesByTemplateType(state, { templateType })
  );

  const handleCreateEmptyReviewTemplate = ({
    version = reviewTemplateBuilderV2 ? ReviewTemplateVersion.two : ReviewTemplateVersion.one,
  }: Partial<{
    version?: ReviewTemplateVersion;
  }> = {}) => {
    toggleIsCreatingNew();
    return dispatch(createReviewTemplate({ name: '', description: '', templateType, version })).then(response => {
      toggleIsCreatingNew();
      return response;
    });
  };

  useEffect(() => {
    if (withFetch) {
      toggleIsFetching();
      dispatch(fetchReviewTemplates({ templateType })).then(() => {
        toggleIsFetching();
      });
    }
  }, [withFetch]);

  return { templates, isFetching, isCreatingNew, handleCreateEmptyReviewTemplate };
};
